import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Table, Column } from '@appkit4/react-components/table';
import { Button } from '@appkit4/react-components/button';
import { Input } from '@appkit4/react-components/field';
import { Loading } from '@appkit4/react-components/loading';
import { Switch } from '@appkit4/react-components/switch';
import useLocale from '../../../locale/useLocale';
import Breadcrumbs from '../../../components/shared/breadcrumbs/Breadcrumbs';
import './DocTypeSettings.scss';
import { AppRoutes } from '../../../Constants';
import useToast from '../../../helpers/useToast';
import useGenericErrorToast from '../../../helpers/useGenericErrorToast';
import type { AxiosError } from 'axios';
import {
    useGetV1DocumentTypeDocumentTypeIdDocumentTypeFields,
    usePostV1AdminUpdateDocumentTypeFields,
    usePostV1AdminToggleValidationEnabled,
} from '../../../api/generated/endpoint';
import type {
    DocumentTypeFieldResponse,
    UpdateDocumentTypeFieldsRequest,
    ToggleValidationEnabledRequest,
} from '../../../api/generated/models';

enum FieldTypes {
    Text = 1,
    Number = 2,
    Date = 3,
    Checkbox = 4,
}

const fieldTypeToString = (fieldTypeId: FieldTypes | undefined): string => {
    switch (fieldTypeId) {
        case FieldTypes.Text:
            return 'Text';
        case FieldTypes.Number:
            return 'Number';
        case FieldTypes.Date:
            return 'Date';
        case FieldTypes.Checkbox:
            return 'Checkbox';
        default:
            return 'Unknown';
    }
};

const FillPDFDocTypeSettings = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { l } = useLocale();
    const showToast = useToast();
    const showGenericErrorToast = useGenericErrorToast();

    const docType = searchParams.get('docType');
    const docTypeId = parseInt(searchParams.get('docTypeId') || '0', 10);

    const [isChanged, setIsChanged] = useState(false);
    const [data, setData] = useState<DocumentTypeFieldResponse[]>([]);
    const [isValidationEnabled, setIsValidationEnabled] = useState(false);

    const processTypeId = parseInt(
        searchParams.get('processTypeId') || '0',
        10
    );

    const {
        data: fieldsData,
        isLoading,
        error,
    } = useGetV1DocumentTypeDocumentTypeIdDocumentTypeFields(docTypeId, {
        processTypeId,
    });

    useEffect(() => {
        if (isChanged == false) {
            if (fieldsData) {
                setData(fieldsData.documentTypeFields ?? []);
                setIsValidationEnabled(fieldsData.validationEnabled ?? false);
            }
        }
    }, [fieldsData]);

    const mutation = usePostV1AdminUpdateDocumentTypeFields();
    const toggleValidationMutation = usePostV1AdminToggleValidationEnabled();

    const handleSave = async () => {
        try {
            const updateRequest: UpdateDocumentTypeFieldsRequest = {
                documentTypeId: docTypeId,
                fields: data.map((field) => ({
                    documentTypeFieldId: field.fieldId,
                    fieldName: field.fieldName,
                    minimumConfidence: field.minimumConfidence,
                    minimumConfidenceActive:
                        field.minimumConfidenceActive ?? false,
                })),
            };

            await mutation.mutateAsync({ data: updateRequest });
            setIsChanged(false);
            showToast(l('_success'), l('_docTypeSettingsSaved'), 'success');
        } catch (error) {
            showGenericErrorToast(error as AxiosError<string, any>);
        }
    };

    const handleToggleValidation = async (checked: boolean) => {
        try {
            const toggleRequest: ToggleValidationEnabledRequest = {
                documentTypeId: docTypeId,
                enableValidation: checked,
            };
            await toggleValidationMutation.mutateAsync({ data: toggleRequest });
            setIsValidationEnabled(checked);
            showToast(l('_success'), l('_validationToggled'), 'success');
        } catch (error) {
            showGenericErrorToast(error as AxiosError<string, any>);
        }
    };

    if (isLoading)
        return <Loading loadingType="circular" indeterminate={true} />;
    if (error) return <div>An error occurred: {error.message}</div>;

    return (
        <div className="doctype-settings">
            <div className="doctype-breadcrumb-container">
                <span
                    className="Appkit4-icon icon-arrow-left-outline"
                    role="button"
                    onClick={() => navigate(AppRoutes.FillPDFSettings)}
                ></span>
                <Breadcrumbs items={[{ label: docType || '', href: '' }]} />
            </div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '24px',
                }}
            >
                <h2 className="ap-typography-heading-m title">{docType}</h2>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}
                    >
                        <span>Enable Validation</span>
                        <Switch
                            checked={isValidationEnabled}
                            onChange={handleToggleValidation}
                        />
                    </div>
                </div>
            </div>
            <div className="table-container">
                <div className="table-title">
                    <span>Document type settings</span>
                </div>
                <Table
                    className="extraction-table"
                    originalData={data}
                    hasTitle
                    condensed
                    style={{ borderRadius: '0px' }}
                >
                    <Column
                        field="fieldName"
                        renderCell={(row) => (
                            <Input
                                style={{ maxWidth: '400px' }}
                                type="text"
                                value={row.fieldName || ''}
                                onChange={(value: string) => {
                                    setIsChanged(true);
                                    const newData = [...data];
                                    const index = newData.findIndex(
                                        (d) => d.fieldId === row.fieldId
                                    );
                                    newData[index].fieldName = value;
                                    setData(newData);
                                }}
                                hideTitleOnInput
                            />
                        )}
                    >
                        Field name
                    </Column>
                    <Column
                        field="fieldTypeId"
                        renderCell={(row) => fieldTypeToString(row.fieldTypeId)}
                    >
                        Field type
                    </Column>
                    <Column
                        field="fieldName"
                        style={{ width: '200px' }}
                        renderCell={(row) => (
                            <span
                                style={{
                                    color: isValidationEnabled
                                        ? '#474747'
                                        : '#c0c0c0',
                                    fontSize: '20px',
                                    cursor: isValidationEnabled
                                        ? 'pointer'
                                        : 'not-allowed',
                                    marginLeft: '60px',
                                }}
                                className="Appkit4-icon icon-setting-outline"
                                role="button"
                                aria-label="Settings"
                                onClick={() => {
                                    if (isValidationEnabled) {
                                        navigate(
                                            `${
                                                AppRoutes.FillPDFValidationSettings
                                            }?docType=${docType}&docTypeId=${docTypeId}&fieldName=${
                                                row.fieldName
                                            }&fieldType=${fieldTypeToString(
                                                row.fieldTypeId
                                            )}&fieldId=${
                                                row.fieldId
                                            }&processTypeId=${processTypeId}`
                                        );
                                    }
                                }}
                            />
                        )}
                    >
                        Validation settings
                    </Column>
                </Table>
                <div className="table-footer">
                    <Button
                        kind="secondary"
                        disabled={!isChanged}
                        onClick={handleSave}
                    >
                        Save
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default FillPDFDocTypeSettings;
